import mustache from "mustache";
import {
  IGetListYearGroupService,
  YearGroup,
} from "../../../abstractions/services/actions/IGetListYearGroupService";
import { BaseItemResponse } from "../../../base/response/BaseItemResponse";
import { IConnectRender } from "../../definitions/IConnectRender";
import { BaseSecondSidebarItemRender } from "./BaseSecondSidebarItemRender";
import template from "./secondSidebarTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let SECOND_SIDEBAR_ID = 0;

/** Base del segundo sidebar agrupado por año (Render/Connect). Automatiza el
 *  ciclo completo: carga los `YearGroup[]` del servicio, puebla el select de
 *  año, monta los items del año elegido (vía `createItem`) y coordina el
 *  acordeón (un item expandido a la vez, el primero auto-seleccionado). La
 *  subclase solo define título, servicio y fábrica de items, más las acciones
 *  opcionales del topbar (`onAdd`/`onSplitView`) y selectores extra. */
export abstract class BaseSecondSidebarRender<T extends BaseItemResponse>
  implements IConnectRender
{
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _sidebarId: number;
  /** Acción del botón volver (la ruta la conoce la página que lo crea). */
  private readonly _onBack: () => void;

  /** @param onBack Acción del botón volver del topbar. */
  constructor(onBack: () => void) {
    SECOND_SIDEBAR_ID += 1;
    this._sidebarId = SECOND_SIDEBAR_ID;
    this._onBack = onBack;
  }

  // ─── Hooks de la subclase ───

  /** Título del topbar (p. ej. "APLICACIONES"). */
  protected abstract get title(): string;

  /** Servicio que provee los items agrupados por año. */
  protected abstract getYearGroupListService(): IGetListYearGroupService<T>;

  /** Fábrica del item de la lista (el contenido expandido es del módulo). */
  protected abstract createItem(item: T): BaseSecondSidebarItemRender;

  /** Acción opcional "agregar": si la subclase la define, aparece el botón +. */
  protected onAdd?(): void;

  /** Acción opcional "vista dividida": si se define, aparece su botón. */
  protected onSplitView?(): void;

  /** Selectores extra bajo el select de año (p. ej. chips de mes). */
  protected renderSelectors(): string {
    return "";
  }

  /** Hook opcional: reacción extra al cambio de año (los items ya se re-montan
   *  solos). */
  protected onYearChanged(_year: number): void {}

  /** Filtra los items del año antes de crear los items concretos (no-op por
   *  defecto). La subclase la sobreescribe cuando agrega un selector extra que
   *  acota la lista (p. ej. mes); en ese caso, llamar `refreshCurrentYear()` al
   *  cambiar el filtro para re-renderizar sin cambiar de año. */
  protected filterItems(items: T[]): T[] {
    return items;
  }

  /** Vuelve a aplicar el año actualmente seleccionado (recalcula
   *  `filterItems`). Pensado para cuando un selector extra (p. ej. mes) cambia
   *  sin que cambie el año. */
  protected refreshCurrentYear(): void {
    if (!this._yearSelect) return;
    this._applyYear(Number(this._yearSelect.value));
  }

  /** Si el sidebar ya fue dispuesto (guarda para cargas async). */
  protected get disposed(): boolean {
    return this._disposed;
  }

  // ─── IConnectRender ───

  /** Esqueleto del sidebar (topbar + selectores + lista). */
  render(): string {
    return mustache.render(template, {
      id: this._sidebarId,
      title: this.title,
      withAdd: !!this.onAdd,
      withSplit: !!this.onSplitView,
      selectors: this.renderSelectors(),
    });
  }

  /** Captura elementos, cablea el topbar y el select de año, e inicia la carga
   *  de los year groups. */
  connect(): void {
    this._backBtn = document.getElementById(`ss-back-${this._sidebarId}`);
    this._addBtn = document.getElementById(`ss-add-${this._sidebarId}`);
    this._splitBtn = document.getElementById(`ss-split-${this._sidebarId}`);
    this._selectorsEl = document.getElementById(
      `ss-selectors-${this._sidebarId}`,
    );
    this._yearSelect = document.getElementById(
      `ss-year-${this._sidebarId}`,
    ) as HTMLSelectElement | null;
    this._listEl = document.getElementById(`ss-list-${this._sidebarId}`);

    this._backBtn?.addEventListener("click", this._onBack);
    if (this.onAdd) this._addBtn?.addEventListener("click", this._onAddClick);
    if (this.onSplitView) {
      this._splitBtn?.addEventListener("click", this._onSplitClick);
    }
    this._yearSelect?.addEventListener("change", this._onYearSelectChange);

    // Selectores extra definidos por la subclase: mostrar el contenedor.
    if (this.renderSelectors().trim() !== "" && this._selectorsEl) {
      this._selectorsEl.style.display = "";
    }

    void this._loadAsync();
  }

  /** Libera listeners e items. */
  dispose(): void {
    this._disposed = true;
    this._backBtn?.removeEventListener("click", this._onBack);
    this._addBtn?.removeEventListener("click", this._onAddClick);
    this._splitBtn?.removeEventListener("click", this._onSplitClick);
    this._yearSelect?.removeEventListener("change", this._onYearSelectChange);
    this._disposeItems();
  }

  // ─── Ciclo interno: cargar → años → items del año ───

  /** Carga los year groups y puebla el select con el primer año expandido. */
  private async _loadAsync(): Promise<void> {
    this._setListMessage("Cargando…");
    try {
      const groups = await this.getYearGroupListService().getListAsync();
      if (this._disposed) return;
      this._groups = groups;

      if (groups.length === 0 || !this._yearSelect) {
        this._setListMessage("Sin registros");
        return;
      }

      this._yearSelect.innerHTML = groups
        .map((group) => `<option value="${group.year}">${group.year}</option>`)
        .join("");
      this._yearSelect.style.display = "";
      if (this._selectorsEl) this._selectorsEl.style.display = "";
      this._applyYear(groups[0].year);
    } catch {
      if (this._disposed) return;
      this._setListMessage("Error al cargar la información.");
    }
  }

  /** Monta los items del año (tras `filterItems`) y notifica a la subclase. */
  private _applyYear(year: number): void {
    const group = this._groups.find((g) => g.year === year);
    const items = this.filterItems(group?.items ?? []);
    this._renderItems(items.map((item) => this.createItem(item)));
    this.onYearChanged(year);
  }

  /** Monta filas en la lista (disponiendo las anteriores), coordina el acordeón
   *  y auto-selecciona el primer item. */
  private _renderItems(items: BaseSecondSidebarItemRender[]): void {
    this._disposeItems();
    if (!this._listEl) return;
    if (items.length === 0) {
      this._setListMessage("Sin registros");
      return;
    }

    this._items = items;
    this._listEl.innerHTML = items.map((item) => item.render()).join("");
    items.forEach((item) => {
      item.connect();
      item.setOnClick(() => this._selectItem(item));
    });
    this._selectItem(items[0]);
  }

  private _backBtn: HTMLElement | null = null;
  private _addBtn: HTMLElement | null = null;
  private _splitBtn: HTMLElement | null = null;
  private _selectorsEl: HTMLElement | null = null;
  private _yearSelect: HTMLSelectElement | null = null;
  private _listEl: HTMLElement | null = null;
  /** Year groups cargados. */
  private _groups: YearGroup<T>[] = [];
  /** Items vivos de la lista. */
  private _items: BaseSecondSidebarItemRender[] = [];
  /** Item actualmente expandido (acordeón). */
  private _itemSelected: BaseSecondSidebarItemRender | null = null;
  private _disposed = false;

  private _onAddClick = (): void => this.onAdd?.();

  private _onSplitClick = (): void => this.onSplitView?.();

  private _onYearSelectChange = (): void => {
    if (!this._yearSelect) return;
    this._applyYear(Number(this._yearSelect.value));
  };

  /** Acordeón: colapsa el expandido y expande el clickeado. */
  private _selectItem(item: BaseSecondSidebarItemRender): void {
    if (this._itemSelected === item) return;
    this._itemSelected?.deselect();
    this._itemSelected = item;
    item.select();
  }

  /** Dispone y descarta los items vivos. */
  private _disposeItems(): void {
    this._items.forEach((item) => item.dispose());
    this._items = [];
    this._itemSelected = null;
  }

  /** Muestra un mensaje de estado en la lista (cargando/vacío/error). */
  private _setListMessage(text: string): void {
    if (this._listEl) {
      this._listEl.innerHTML = `<div class="sx-empty">${text}</div>`;
    }
  }
}
